import { useEffect, useState } from 'react';
import { Eye } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useAuth, hasRole } from '../lib/auth';

/**
 * ⚙ de "quién recibe las segundas opiniones" — arriba a la derecha de la cola de Claim Review.
 *
 * Hoy es Jessika, pero eso cambia (se va de vacaciones, entra otro senior), así que el nombre
 * NO va en el código: se elige acá. Cambiarlo es una decisión de la empresa → super-manager;
 * el resto lo ve en modo lectura, que es lo que necesitan para saber a quién le están pidiendo.
 */
type Setting = { userId: string | null; userName: string | null };
type UserOpt = { id: string; name: string };

export function SecondOpinionSetting() {
  const { t } = useI18n();
  const { me } = useAuth();
  const puedeEditar = hasRole(me, 'super-manager');
  const [abierto, setAbierto] = useState(false);
  const [cfg, setCfg] = useState<Setting | null>(null);
  const [users, setUsers] = useState<UserOpt[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<Setting>('/api/claim-review/second-opinion-user').then(setCfg).catch(() => {});
  }, []);
  useEffect(() => {
    if (!abierto || !puedeEditar || users.length) return;
    api.get<{ users: UserOpt[] }>('/api/team/mention-targets').then((r) => setUsers(r.users || [])).catch(() => {});
  }, [abierto, puedeEditar, users.length]);

  async function guardar(userId: string) {
    setBusy(true);
    try { setCfg(await api.put<Setting>('/api/claim-review/second-opinion-user', { userId: userId || null })); }
    catch { /* ignore */ } finally { setBusy(false); }
  }

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button className="btn ghost sm" onClick={() => setAbierto((v) => !v)}
        title={cfg?.userName ? t('so.goesTo', { name: cfg.userName }) : t('so.notSetShort')}>
        <Eye size={13} strokeWidth={1.75} /> {cfg?.userName || t('so.notSetShort')}
      </button>
      {abierto && (
        <span className="card" style={{ position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 40, minWidth: 240, display: 'grid', gap: 6, padding: 10 }}>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], textTransform: 'uppercase', letterSpacing: 0.4 }}><Eye size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('so.settingTitle')}</span>
          {puedeEditar ? (
            <select className="input sm" value={cfg?.userId ?? ''} disabled={busy} onChange={(e) => guardar(e.target.value)}>
              <option value="">— {t('so.nobody')} —</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          ) : (
            <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'] }}>{cfg?.userName || t('so.nobody')}</span>
          )}
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('so.settingHint')}</span>
          <button className="btn ghost sm" onClick={() => setAbierto(false)}>{t('cdw.close')}</button>
        </span>
      )}
    </span>
  );
}
